/**
 * Centralized Payment Methods Configuration
 * 
 * Controls which payment methods are displayed to users on the landing page
 * and subscription pages. This allows easy toggling of payment methods that
 * are "coming soon" or temporarily unavailable.
 */

export interface PaymentMethod {
  id: string;
  name: string;
  icon: string; // Icon name or symbol
  color: string; // Tailwind color class
  enabled: boolean;
  comingSoon?: boolean;
  description?: string;
  processingFee?: string;
}

export interface PaymentConfig {
  methods: PaymentMethod[];
  processor: string;
  processorNote?: string;
}

// Payment methods configuration
export const PAYMENT_CONFIG: PaymentConfig = {
  processor: 'Stripe',
  processorNote: 'All payments are processed securely through Stripe',
  
  methods: [
    {
      id: 'cards',
      name: 'Credit & Debit Cards',
      icon: 'CreditCard', // Lucide icon name
      color: 'text-blue-400',
      enabled: true,
      description: 'Visa, Mastercard, American Express, and more'
    },
    {
      id: 'bitcoin',
      name: 'Bitcoin',
      icon: '₿', // Symbol/text
      color: 'text-orange-400',
      enabled: false, // Currently disabled - coming soon
      comingSoon: true,
      description: 'Pay with Bitcoin (BTC)'
    },
    {
      id: 'ethereum',
      name: 'Ethereum',
      icon: 'Ξ', // Symbol/text  
      color: 'text-purple-400',
      enabled: false, // Currently disabled - coming soon
      comingSoon: true,
      description: 'Pay with Ethereum (ETH)'
    }
  ]
};

// Helper functions
export function getEnabledPaymentMethods(): PaymentMethod[] {
  return PAYMENT_CONFIG.methods.filter(method => method.enabled);
}

export function getAllPaymentMethods(): PaymentMethod[] {
  return PAYMENT_CONFIG.methods;
}

export function getPaymentMethod(id: string): PaymentMethod | undefined {
  return PAYMENT_CONFIG.methods.find(method => method.id === id);
}

export function isPaymentMethodEnabled(id: string): boolean {
  const method = getPaymentMethod(id);
  return method?.enabled ?? false;
}

export function getComingSoonPaymentMethods(): PaymentMethod[] {
  return PAYMENT_CONFIG.methods.filter(method => method.comingSoon && !method.enabled);
}

export function getDisplayPaymentMethods(): PaymentMethod[] {
  // Returns enabled methods plus "coming soon" methods for display
  return PAYMENT_CONFIG.methods.filter(method => method.enabled || method.comingSoon);
}